import { usePageScrollRestoration } from "~/common/usePageScrollRestoration"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  BookOutlined,
  ClearOutlined,
  GiftOutlined,
  HomeOutlined,
  MoonOutlined,
  PlaySquareOutlined,
  ScanOutlined,
  StarFilled,
  SunOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
  MessageOutlined,
  WechatOutlined,
} from "@ant-design/icons"
import { Avatar, Button, Flex, Layout, Menu, Popover, Tooltip } from "antd"
import type { MenuProps } from "antd"
import classNames from "classnames"
import { Outlet, useLocation, useNavigate } from "react-router"
import {
  AnalyticsAction,
  ContactPopover,
  PointDetailModal,
  RechargeUpgradeModal,
  ResourceLibraryModal,
  useAnalyticsEvent,
  Text,
  ModelLaunchNotice,
  UserAvatarIcon,
  Tag,
} from "~/components"
import type { RechargeModalTabKey } from "~/components/RechargeUpgradeModal"
import { FIRST_VIDEO_GIFT_POINTS, WalletRechargeType } from "~/common/constants"
import { PRODUCT_TUTORIAL_URL } from "~/common/links"
import { ROUTE_META, ROUTER } from "~/common/router"
import { useSessionStore } from "~/store/session"
import { useThemeStore } from "~/store/theme"
import { getWalletBalanceSummary } from "~/api/user"
import { CommunityPopover } from "./CommunityPopover"
import styles from "./index.css"

const RECREATE_MENU_KEY = "recreate-menu"
const getMenuItems = (): MenuProps["items"] => [
  {
    key: ROUTE_META.home.path,
    icon: <HomeOutlined />,
    label: ROUTE_META.home.label,
  },
  {
    key: RECREATE_MENU_KEY,
    icon: <PlaySquareOutlined />,
    label: "爆款视频复刻",
    children: [
      {
        key: ROUTE_META.recreate.path,
        label: ROUTE_META.recreate.label,
      },
      {
        key: ROUTE_META.recreateV2.path,
        label: ROUTE_META.recreateV2.label,
      },
      {
        key: ROUTE_META.oneClickCopy.path,
        label: ROUTE_META.oneClickCopy.label,
      },
    ],
  },
  {
    key: ROUTE_META.analysis.path,
    icon: <ScanOutlined />,
    label: ROUTE_META.analysis.label,
  },
  {
    key: ROUTE_META.videoSubtitleErasure.path,
    icon: <ClearOutlined />,
    label: ROUTE_META.videoSubtitleErasure.label,
  },
  {
    key: ROUTE_META.videoEnhancement.path,
    icon: <ThunderboltOutlined />,
    label: ROUTE_META.videoEnhancement.label,
  },
  {
    key: ROUTE_META.tasks.path,
    icon: <UnorderedListOutlined />,
    label: ROUTE_META.tasks.label,
  },
]

const lightLogoUrl =
  "https://img.alicdn.com/bao/uploaded/i1/2829330402/O1CN018PWYRg1EqAMgM8lUo_!!4611686018427381730-0-item_pic.jpg"
const darkLogoUrl =
  "https://img.alicdn.com/bao/uploaded/i3/2829330402/O1CN01GfyWMS1EqAMgM9Zb0_!!4611686018427381730-2-item_pic.png"
const REGISTRATION_GIFT_VALID_DAYS = 3

type ActiveAccountModal = "points" | "recharge" | "resources" | null

const getUserDisplayName = (user: API.User.SessionUser | undefined) => {
  if (!user) return "未登录"

  return `用户${user.mobile.slice(-6)}`
}

const getRegistrationGiftExpiresAt = (createdAt?: string) => {
  if (!createdAt) return ""

  const expiresAt = new Date(createdAt)

  if (Number.isNaN(expiresAt.getTime())) return ""

  expiresAt.setDate(expiresAt.getDate() + REGISTRATION_GIFT_VALID_DAYS)

  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(expiresAt)
}

export const AppLayout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const contentRef = useRef<HTMLDivElement>(null)
  usePageScrollRestoration(contentRef, location.key)
  const sendRecreateV2NavigationAnalytics = useAnalyticsEvent({
    category: "",
    prefix: "左侧导航栏",
  })
  const mode = useThemeStore((state) => state.mode)
  const setPreference = useThemeStore((state) => state.setPreference)
  const logout = useSessionStore((state) => state.logout)
  const openLoginModal = useSessionStore((state) => state.openLoginModal)
  const isProfessional = useSessionStore((state) => state.isProfessional)
  const user = useSessionStore((state) => state.user)
  const userIdentity = user?.id || user?.mobile
  const menuItems = useMemo(() => getMenuItems(), [])
  const [activeAccountModal, setActiveAccountModal] =
    useState<ActiveAccountModal>(null)
  const [rechargeModalTab, setRechargeModalTab] =
    useState<RechargeModalTabKey>("recharge")
  const [registrationGiftPoints, setRegistrationGiftPoints] = useState(0)
  const isHomePage = location.pathname === ROUTER.HOME
  const selectedKeys = useMemo(() => {
    const selectedItem = Object.values(ROUTE_META).find((item) => {
      if (item.path === ROUTER.HOME) {
        return location.pathname === ROUTER.HOME
      }

      return (
        location.pathname === item.path ||
        location.pathname.startsWith(`${item.path}/`)
      )
    })

    return selectedItem ? [selectedItem.path] : []
  }, [location.pathname])

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === ROUTER.RECREATE_V2) {
      sendRecreateV2NavigationAnalytics("新复刻流程-单品展示类")
    }

    navigate(key)
  }

  const userName = getUserDisplayName(user)
  const pointBalance = user?.balance_info?.available_amount || 0
  const planLabel = isProfessional ? "专业版" : "免费版"
  const themeActionLabel = mode === "dark" ? "切换到浅色模式" : "切换到深色模式"
  const registrationGiftExpiresAt = getRegistrationGiftExpiresAt(
    user?.created_t,
  )
  const showNewUserExclusive = Boolean(user && registrationGiftPoints !== 0)

  useEffect(() => {
    if (!userIdentity) {
      setRegistrationGiftPoints(0)
      return
    }

    let ignore = false

    const loadRegistrationGiftPoints = async () => {
      try {
        const response = await getWalletBalanceSummary()

        if (ignore) return

        const registrationGift = response.data?.balance_detail?.find(
          (item) =>
            item.wallet_recharge_type === WalletRechargeType.registrationGift,
        )

        setRegistrationGiftPoints(registrationGift?.available_amount ?? 0)
      } catch {
        if (ignore) return

        setRegistrationGiftPoints(0)
      }
    }

    void loadRegistrationGiftPoints()

    return () => {
      ignore = true
    }
  }, [userIdentity])

  const openRechargeModal = (tab: RechargeModalTabKey = "recharge") => {
    setRechargeModalTab(tab)
    setActiveAccountModal("recharge")
  }

  const handleRechargeShortcut = () => {
    if (!user) {
      openLoginModal()
      return
    }

    openRechargeModal(isProfessional ? "recharge" : "upgrade")
  }

  const accountPopoverContent = (
    <Flex
      className={styles.accountPopover}
      vertical
      gap={14}
    >
      <Flex vertical>
        <Button
          className={styles.popoverAction}
          type="text"
          onClick={() => setActiveAccountModal("points")}
        >
          积点明细
        </Button>
        <Button
          className={styles.popoverAction}
          type="text"
          onClick={() => navigate(ROUTER.ORDERS)}
        >
          订单记录
        </Button>
        <Button
          className={styles.popoverAction}
          type="text"
          onClick={() => setActiveAccountModal("resources")}
        >
          我的资源
        </Button>
        <Button
          className={styles.popoverAction}
          type="text"
          onClick={() => navigate(ROUTER.TEMPLATE_FAVORITES)}
        >
          我的收藏
        </Button>
        <Flex
          className={styles.popoverActionRow}
          align="center"
          justify="space-between"
        >
          <span>主题</span>
          <Tooltip title={themeActionLabel}>
            <Button
              aria-label={themeActionLabel}
              icon={mode === "dark" ? <MoonOutlined /> : <SunOutlined />}
              shape="circle"
              size="small"
              type="text"
              onClick={() => setPreference(mode === "dark" ? "light" : "dark")}
            />
          </Tooltip>
        </Flex>
        {user ? (
          <Button
            className={styles.popoverAction}
            type="text"
            danger
            onClick={async () => {
              try {
                await logout()
              } catch {
                // 请求层已统一提示错误
                return
              }
              if (user.switch_user_id) {
                window.location.reload()
              }
            }}
          >
            退出登录
          </Button>
        ) : null}
      </Flex>
    </Flex>
  )

  const handleAccountClick = () => {
    if (!user) {
      openLoginModal()
    }
  }

  return (
    <Layout
      className={styles.layout}
      style={{ colorScheme: mode }}
    >
      <ModelLaunchNotice />
      <Layout>
        <Layout.Sider
          className={styles.sider}
          theme={mode}
          width="auto"
        >
          <Flex
            orientation="vertical"
            justify="space-between"
            style={{ height: "100%", minHeight: 0 }}
          >
            <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <img
                style={{ padding: "16px 16px 0" }}
                width={148}
                src={mode === "dark" ? darkLogoUrl : lightLogoUrl}
                alt=""
              />
              <Menu
                className={styles.menu}
                defaultOpenKeys={[RECREATE_MENU_KEY]}
                theme={mode}
                mode="inline"
                items={menuItems}
                selectedKeys={selectedKeys}
                onClick={handleMenuClick}
              />
            </div>
            <Flex
              vertical
              gap={12}
            >
              {showNewUserExclusive ? (
                <button
                  className={styles.newUserCard}
                  type="button"
                  onClick={() => navigate(ROUTER.RECREATE)}
                >
                  <div
                    aria-hidden
                    className={styles.newUserDecoration}
                  />
                  <Text
                    className={styles.newUserBadge}
                    size="s"
                  >
                    <GiftOutlined style={{ marginRight: 4 }} />
                    新用户专享
                  </Text>
                  <Text>
                    免费赠送&nbsp;
                    {FIRST_VIDEO_GIFT_POINTS} 积点，剩余&nbsp;
                    <Text
                      color="primary"
                      strong
                      size="l"
                    >
                      {registrationGiftPoints} 积点
                    </Text>
                  </Text>
                  {registrationGiftExpiresAt ? (
                    <Text
                      size="s"
                      type="secondary"
                    >
                      <StarFilled
                        aria-hidden
                        className={styles.newUserExpireIcon}
                      />
                      {registrationGiftExpiresAt}到期
                    </Text>
                  ) : null}
                </button>
              ) : null}
              <Flex
                className={styles.accountShortcuts}
                gap={4}
                vertical
              >
                <CommunityPopover>
                  <Button
                    className={styles.accountShortcut}
                    icon={<WechatOutlined />}
                    type="text"
                  >
                    加入微信群
                    <span className={styles.recommendTag}>推荐</span>
                  </Button>
                </CommunityPopover>
                <ContactPopover placement="rightBottom">
                  <Button
                    className={styles.accountShortcut}
                    icon={<MessageOutlined />}
                    type="text"
                  >
                    联系客服
                  </Button>
                </ContactPopover>
                <AnalyticsAction
                  action={isProfessional ? "点击充值" : "点击升级"}
                  category="账户体系"
                  prefix={isProfessional ? "充值积分" : "版本升级"}
                >
                  <Button
                    className={classNames(
                      styles.accountShortcut,
                      styles.rechargeShortcut,
                    )}
                    icon={
                      <img
                        width={14}
                        src="https://img.alicdn.com/bao/uploaded/i1/2829330402/O1CN01G9unEm1EqAMf3CEal_!!4611686018427381730-2-item_pic.png"
                        alt=""
                      />
                    }
                    type="text"
                    onClick={handleRechargeShortcut}
                  >
                    {isProfessional ? "充值积点" : "升级/充值"}
                  </Button>
                </AnalyticsAction>
                <Button
                  className={styles.accountShortcut}
                  icon={<BookOutlined />}
                  type="text"
                  onClick={() =>
                    window.open(
                      PRODUCT_TUTORIAL_URL,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  产品教程
                </Button>
              </Flex>
              <Popover
                content={accountPopoverContent}
                placement="topLeft"
                trigger="hover"
              >
                <Flex
                  align="center"
                  className={styles.accountTrigger}
                  gap={8}
                  onClick={handleAccountClick}
                >
                  <Avatar
                    icon={<UserAvatarIcon className={styles.userAvatarIcon} />}
                    size={32}
                    className={styles.userAvatar}
                  />
                  <Flex
                    className={styles.userMeta}
                    vertical
                  >
                    <Flex
                      align="center"
                      gap={6}
                      wrap={false}
                    >
                      <Text className={styles.userName}>{userName}</Text>
                    </Flex>
                    {user ? (
                      <Flex
                        align="center"
                        gap={8}
                      >
                        <Tag color={isProfessional ? "primary" : "default"}>
                          {planLabel}
                        </Tag>
                        <Text
                          size="s"
                          className={styles.pointInline}
                        >
                          积点 {pointBalance}
                        </Text>
                      </Flex>
                    ) : null}
                  </Flex>
                </Flex>
              </Popover>
            </Flex>
          </Flex>
        </Layout.Sider>
        <Layout.Content
          className={styles.content}
          ref={contentRef}
        >
          <Flex
            className={classNames(styles.page, isHomePage && styles.homePage)}
            vertical
          >
            <Outlet />
          </Flex>
        </Layout.Content>
        <ResourceLibraryModal
          open={activeAccountModal === "resources"}
          mode="manage"
          onCancel={() => setActiveAccountModal(null)}
        />
        <RechargeUpgradeModal
          defaultTab={rechargeModalTab}
          open={activeAccountModal === "recharge"}
          onCancel={() => setActiveAccountModal(null)}
        />
        <PointDetailModal
          open={activeAccountModal === "points"}
          onCancel={() => setActiveAccountModal(null)}
          onRecharge={() => openRechargeModal()}
        />
      </Layout>
    </Layout>
  )
}
